/* eslint-disable jsdoc/check-param-names */
/* eslint-disable camelcase */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ALLOWED_USER_REGISTRATION_FIELDS, ALLOWED_PROFILE_FIELDS } from 'lib/constants';
import { AUTHENTICATED, UNAUTHENTICATED, UNINITIALIZED } from 'lib/myNewsConstants';
import { SCREENS } from 'lib/loginFormConstants';
import { stub as $t } from '@nbcnews/analytics-framework';
import { updateUserPreferences } from '@nbcnews/flintstone';
import { setCookie, getCookie, deleteCookie } from 'lib/cookies';
import { newsletterData } from 'components/AccountPage/Newsletters/data';
import { MYNEWS_ENABLED } from 'lib/brandFeatures';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { bedrockApi as api, decodeJWT } from './utils';

/** @type {"today" | "news"} Brand */

export const INITIAL_STATE = {
  user: {
    user_id: null,
    email: '',
    email_verified: false,
    last_name: '',
    first_name: '',
    birth_year: '',
    gender: '',
    address_line1: '',
    address_line2: null,
    state: '',
    zipcode: '',
    phone_number: '',
    password: '',
  },
  authenticationState: UNINITIALIZED,
  access_token: null,
  tinypass_token: null,
  brand: '',
  loading: false,
  error: '',
  showAuthenticationModal: false,
  screen: SCREENS.START,
  loginStarted: false,
  social: {
    idToken: '',
    firstName: '',
    platform: '',
    isNewUser: false,
  },
  signInType: '',
  showGate: true,
  newsletterSubscriptions: [],
  hasSubscribedToAll: false,
  pageView: null,
};

/**
 * Creates the zustand store with initial state and actions for the Bedrock Registration service
 * @param {Function} set - The function to update the state.
 * @param {Function} get - The function to get the current state.
 * @returns {object} The store with state and actions.
 */
export const bedrockRegistrationStore = (set, get) => ({
  ...INITIAL_STATE,

  /** *** GLOBAL FUNCTIONS *** */

  /**
   * Set user state back to default values to wipe user data
   */
  reset: () => {
    set({ ...INITIAL_STATE }, false, 'reset');
    // Clear the cookies
    deleteCookie('access_token');
    deleteCookie('user_name');
    deleteCookie('SESSION');
    deleteCookie('bedrock_session');
    get().setTinypassToken(null);
  },

  /**
   * Sets the loading state in the store.
   * @param {boolean} loading - The loading state.
   * @returns {void}
   */
  setLoading: (loading) => set({ loading }, false, 'setLoading'),

  /**
   * Sets the error
   * @param {string} error error message
   * @returns {void}
   */
  setError: (error) => set({ error }, false, 'setError'),


  /** *** AUTHENTICATION STATE HANDLING *** */

  /**
   * Returns access token and bedrock session, otherwise falls back to cookies.
   * @returns {{access_token: string|null, bedrock_session: string|null}}
   */
  getAuthTokens: () => ({
    access_token: get().access_token || getCookie('access_token'),
    bedrock_session: getCookie('bedrock_session'),
  }),

  /**
   * Sets the authentication state in the store.
   * @param {string} authenticationState - The authentication state.
   * @returns {void}
   */
  setAuthenticationState: (authenticationState) => {
    if (authenticationState !== UNINITIALIZED) {
      get().setLoading(false);
    }
    // reset cookie value for user_name if NOT authenticated
    const user = { ...get().user };
    if (authenticationState === UNAUTHENTICATED) {
      $t('track', 'ramen_authflow', {
        action: 'PageLoad',
        authState: authenticationState,
        userId: user?.user_id,
      });
      get().reset();
    }

    if (authenticationState === AUTHENTICATED) {
      $t('track', 'ramen_authflow', {
        action: 'PageLoad',
        authState: authenticationState,
        userId: user?.user_id,
      });
      // if user isn't in the log in form flow, dismiss the gate by default
      if (!get().loginStarted) {
        set({ showGate: false }, false, 'setAuthenticationState');
      }
    }
    set({ authenticationState }, false, 'setAuthenticationState');
  },

  /**
   * Refreshes the user access token
   *  @returns {object} The user ID value
   */
  refreshUserToken: async () => {
    // check if user is already is logged in
    const access_token = getCookie('access_token');

    if (access_token) {
      const { sub: userId, brand, scope } = decodeJWT(access_token);

      if (userId) {
        // set user_id in store so it's available for MyNews API calls
        set({ user: { ...get().user, user_id: userId }, brand }, false, 'refreshUserToken');

        // fallback in case the user doesn't have write scope
        const shouldRefreshToken = scope !== 'users:read_write' || get().isTokenExpired(access_token);

        // if access token is expired, refresh it
        if (shouldRefreshToken) {
          try {
            await get().getUserToken(userId);
          } catch (err) {
            set({ error: err.message }, false, 'refreshUserTokenError');
            get().setAuthenticationState(UNAUTHENTICATED);
          }
        } else {
          // set user as authenticated when they have a valid token
          get().setAuthenticationState(AUTHENTICATED);
        }
      } else {
        // set as unauthenticated if no user id is found in token
        get().setAuthenticationState(UNAUTHENTICATED);
      }
    } else {
      // set as unauthenticated if no access token is found
      get().setAuthenticationState(UNAUTHENTICATED);
    }
  },
  /**
   * Checks if the token is expired.
   * @param {string} token - The JWT token.
   * @returns {boolean} The value of the `isExpired` flag.
   */
  isTokenExpired: (token = '') => {
    const payloadBase64 = token.split('.')[1];
    if (!payloadBase64) {
      return {};
    }
    try {
      const decodedJson = Buffer.from(payloadBase64, 'base64').toString();
      const decoded = JSON.parse(decodedJson);
      const { exp } = decoded;
      const tokenExpiry = exp * 1000;
      const isExpired = Date.now() >= tokenExpiry;
      return isExpired;
    } catch (error) {
      return true;
    }
  },

  /**
  * Sets the tinypass token in the store and cookie.
  * @param {string|null} token - The tinypass token to set. If null, the cookie will be deleted.
  */
  setTinypassToken: (token) => {
    if (token) setCookie('tinypass_token', token);
    else deleteCookie('tinypass_token');
    if (typeof window !== 'undefined') {
      window.tp = window.tp || [];
      window.tp.userRef = token || null;
    }
    set({ tinypass_token: token }, false, 'setTinypassToken');
  },

  /**
   * Retrieves a new user token from the API.
   * @param {string} userId - The ID of the user.
   * @returns {Promise<void>} Resolves when the token is successfully retrieved.
   */
  getUserToken: async (userId) => {
    try {
      const response = await api(`/users/${userId}/auth/token`, {
        method: 'POST',
        body: {
          bedrock_session: getCookie('bedrock_session'),
          scope: 'users:read_write',
          brand: get().brand,
        },
      });
      if (response?.success && response?.data?.access_token) {
        set({ access_token: response.data.access_token }, false, 'refreshUserToken');
        setCookie('access_token', response.data.access_token);
        // also update tinypass token if available
        get().setTinypassToken(response.data?.tinypass_token);
        // set user as authenticated when they have a valid token
        get().setAuthenticationState(AUTHENTICATED);
      } else {
        get().setAuthenticationState(UNAUTHENTICATED);
      }
    } catch (err) {
      set({ error: err.message }, false, 'refreshUserToken');
      get().setAuthenticationState(UNAUTHENTICATED);
      throw err;
    }
  },


  /** *** LOGIN / REGISTRATION FLOW *** */

  /**
   * Sets the screen
   * @param {string} screen screen
   * @param {object} [userFormData={}] - Optional user form data to prefill the screen.
   * @returns {void}
   */
  setScreen: (screen, userFormData = {}) => {
    set({ screen, error: '', loginStarted: true }, false, 'setScreen');

    if (screen === SCREENS.LOGIN_SUCCESS) {
      set({ showGate: false }, false, 'setScreen');
    }

    if (screen === SCREENS.LOGIN) {
      set({
        user: {
          email: userFormData.email || '',
          password: userFormData.password || '',
        },
      }, false, 'setScreen');
    }
  },

  /**
   * Sets the AuthenticationModal state in the store to show or hide the log in modal.
   * @param {boolean} showAuthenticationModal defaults to showing the modal
   */
  setAuthenticationModal: (showAuthenticationModal = true) => set({ showAuthenticationModal }, false, 'setAuthenticationModal'),

  /**
   * Searches for a user by email and vertical/brand
   * @param {string} email The email of the user.
   * @param {import('lib/graphqlTypeDefs.mjs').OrganizationType} brand - The brand name associated with the user.
   * @returns {Promise<object>} The response from the API.
   */
  searchUserByEmail: async (email, brand) => {
    get().setLoading(true);
    set({ brand }, false, 'searchUserByEmail');
    try {
      const response = await api('/users/search/profile', {
        body: {
          email,
          brand,
        },
      });
      get().setLoading(false);
      return response;
    } catch (error) {
      get().setLoading(false);
      return error;
    }
  },
  /**
   * Sets the email in the store.
   * @param {string} email
   */
  setEmail: (email) => set({ user: { email } }, false, 'setEmail'),

  /**
   * @param {object} props - The user registration props.
   * @param {string} props.method - The method used for tracking, in the form ELEMENT:ACTION ('SubmitButton:Click').
   * @param {object} props.registrationObj - The registration object containing user details.
   * @param {string} props.registrationObj.first_name - The user first name.
   * @param {string} props.registrationObj.last_name - The user last name.
   * @param {string} props.registrationObj.email - The user email.
   * @param {string} props.registrationObj.password - The user password.
   * @param {string} props.registrationObj.birth_year - The user birth year.
   * @param {import('lib/graphqlTypeDefs.mjs').OrganizationType} props.registrationObj.brand - The brand name associated with the user.
   */
  userRegistration: async ({ method, registrationObj = {} }) => {
    let status;
    get().setLoading(true);

    const body = Object.fromEntries(
      ALLOWED_USER_REGISTRATION_FIELDS
        .map((field) => [field, registrationObj[field]])
        .filter(([, value]) => value !== undefined),
    );

    try {
      const response = await api('/users/register', {
        body,
      });

      // Successful Registration
      if (response?.success) {
        get().track({
          action: 'SignUp:Success',
          method,
          authState: 'authenticated',
          userId: response?.data?.user_id,
        });

        get().setLoading(false);
        set({
          signInType: 'registration',
        }, false, 'registrationSuccess');

        status = SCREENS.VERIFY_OTC;
      } else {
        // something went wrong with the api request, set an error
        set({ error: response?.error?.user_message }, false, 'registrationError');
      }
      // Api error
    } catch (err) {
      set({ error: 'There was an error registering' }, false, 'registrationError');
    }
    get().setLoading(false);
    return status;
  },

  /**
   * Checks user password for login
   * @param {object} props - The properties passed for user sign-in.
   * @param {string} props.email - The user password.
   * @param {string} props.password - The user password.
   * @param {string} props.password - The user password.
   * @param {Brand} props.brand - The brand the user is signing in under
   * @param {object} props.trackingPayload - The tracking payload object.
   * @param {string} props.trackingPayload.action - The action to track.
   * @param {string} props.trackingPayload.method - The method to track.
   * @param {import('lib/graphqlTypeDefs.mjs').OrganizationType} props.brand - The brand name associated with the user.
   * @returns {string} The status of the login.
   */
  userSignin: async ({
    email, password, brand, trackingPayload,
  }) => {
    let status;

    get().setLoading(true);

    try {
      get().track(trackingPayload);
      const response = await api('/users/signin', {
        body: {
          email: email || get().user.email,
          password,
          brand: brand || get().brand,
          remember_me: false,
        },
      });

      if (response?.success) {
        // Only update user properties defined in initial state
        const userData = Object.fromEntries(
          Object.keys(INITIAL_STATE.user)
            .map((field) => [field, response.data[field]])
            .filter(([, value]) => !!value),
        );

        const user = { ...get().user, ...userData };
        get().track({
          action: 'SignIn:Success',
          method: `${trackingPayload.method}`,
          userId: user.user_id,
        });
        get().setTinypassToken(response.data?.tinypass_token);

        // user has an email registered with an account, send them to the login screen
        if (!user.email) {
          user.email = email || get().user.email;
        }
        set({
          user,
          access_token: response?.data?.access_token,
          signInType: 'login',
          brand,
        }, false, 'userSignin');

        if (response?.data?.access_token) {
          get().fetchUserProfile(response?.data?.access_token);
        }

        status = await get().getLoginSuccessScreen(response?.data, brand);
      } else {
        // something went wrong with the api request, set an error
        set({ error: response?.error?.user_message }, false, 'userSigninError');
      }
    } catch (err) {
      // something went wrong with the call to the api, set an error
      set({ error: 'There was an error logging in' }, false, 'userSigninCallError');
      get().setLoading(false);
      return status;
    }
    get().setLoading(false);
    return status;
  },

  /**
   * Handles social login
   * @param {object} props - The social login props.
   * @param {object} props.socialUser - The social user object containing user details.
   * @param {string} props.socialUser.idToken - The social user ID token.
   * @param {string} props.socialUser.firstName - The social user first name.
   * @param {boolean} props.socialUser.isNewUser - Whether the user is new or not.
   * @param {import('lib/graphqlTypeDefs.mjs').OrganizationType} props.brand - The brand name associated with the user.
   * @param {string} props.platform - The platform of the social login (e.g. 'google', 'apple').
   * @param {object} props.trackingPayload - The tracking payload object.
   * @param {string} props.trackingPayload.action - The action to track.
   * @param {string} props.trackingPayload.method - The method to track.
   * @returns {string} The status of the social login.
   */
  socialLogin: async ({
    socialUser, brand, platform, trackingPayload,
  }) => {
    let status;
    get().setLoading(true);

    // data returned from the social login response
    const {
      idToken,
      firstName,
      isNewUser,
    } = socialUser;

    if (isNewUser) {
      // direct new users to the registration screen before logging in
      set({ social: { idToken, firstName, platform }, brand }, false, 'socialLogin');
      get().setLoading(false);
      return SCREENS.SOCIAL_REGISTRATION;
    }

    try {
      // track the start of the social login process
      get().track(trackingPayload);
      const response = await api('/users/social/signin', {
        body: {
          id_token: idToken,
          brand,
          platform,
        },
      });

      if (response?.success) {
        // track successful login
        const successPayload = {
          action: 'SignIn:Success',
          method: `${trackingPayload.method}`,
          userId: response?.data?.user_id,
        };
        get().track(successPayload);
        // user successfully logs in with social login
        set({ user: response?.data }, false, 'loginSuccess');
        get().setTinypassToken(response.data?.tinypass_token);

        get().fetchUserProfile(response?.data?.access_token);
        if (
          getFeatureConfigForBrand(MYNEWS_ENABLED, brand)?.accountCreationSuccesstype === 'news'
        ) {
          status = SCREENS.SELECT_INTERESTS;
        } else if (!response?.data.first_name) {
          set({ signInType: 'login' });
          status = SCREENS.USER_NAME;
        } else status = SCREENS.LOGIN_SUCCESS;
      } else {
        // something went wrong with the api request, set an error
        set({ error: response?.error?.user_message }, false, 'loginError');
      }
    } catch (err) {
      set({ error: 'There was an error logging in' }, false, 'loginError');
      get().setLoading(false);
    }
    get().setLoading(false);
    return status;
  },

  /**
   * Handles user registration with social login
   * @param {object} props - The user registration props.
   * @param {object} props.trackingPayload - The tracking payload object.
   */
  userSocialRegistration: async ({ trackingPayload }) => {
    let status;

    try {
      // track the start of the social registration process
      get().track(trackingPayload);
      const response = await api('/users/social/signin', {
        body: {
          id_token: get().social.idToken,
          brand: get().brand,
          platform: get().social.platform,
          first_name: get().social.firstName,
        },
      });

      if (response?.success) {
        // track successful registration
        const successPayload = {
          action: 'SignIn:Success',
          // same method as the initial tracking payload
          method: `${trackingPayload.method}`,
          userId: response?.data?.user_id,
        };
        get().track(successPayload);

        set({ user: response?.data?.access_token }, false, 'userSocialRegistration');
        get().setTinypassToken(response.data?.tinypass_token);
        get().fetchUserProfile(response?.data?.access_token);
        if (!response?.data?.first_name) {
          set({ signInType: 'registration' });
          status = SCREENS.USER_NAME;
        } else status = SCREENS.REGISTRATION_SUCCESS;
      } else {
        // something went wrong with the api request, set an error
        set({ error: response?.error?.user_message }, false, 'userSocialRegistration');
      }
    } catch (err) {
      set({ error: 'There was an error registering' }, false, 'userSocialRegistration');
      return status;
    }
    return status;
  },

  /**
   * Sends the user a one time code for login verification
   * @param {object} props - The tracking props object.
   * @param {string} [props.email] - The email address to send the code to.
   * @param {string} [props.brand] - The brand/vertical for the request.
   * @param {object} props.trackingPayload - The tracking payload object.
   * @param {string} props.trackingPayload.action - The action to track.
   * @param {string} props.trackingPayload.method - The method to track.
   * @returns {void}
   */
  requestOneTimeCode: async ({ email = null, brand = null, trackingPayload }) => {
    let status;
    try {
      // track the start of the one time code request process
      get().track(trackingPayload);
      const response = await api('/users/otp/request', {
        body: {
          email: email || get().user.email,
          brand: brand || get().brand,
        },
        headers: {
          Authorization: `Bearer ${get().clientToken}`,
        },
      });
      if (response?.success) {
        // send user to the verify one time code screen
        status = SCREENS.LOGIN_OTC;
      } else {
        // something went wrong with the api request, set an error
        set({ error: response?.error?.user_message }, false, 'oneTimeCode');
      }
    } catch (err) {
      set({ error: 'There was an error, please try again' }, false, 'oneTimeCode');
    }

    return status;
  },

  /**
   * Identifies a user with a one time codes
   * @param {string} oneTimeCode - The one time code sent to the user's email
   * @param {import('lib/graphqlTypeDefs.mjs').OrganizationType} brand - The brand name associated with the user.
   * @param {string} email - The email of the user.
   * @returns {string} The status of the one time code.
   */
  verifyOneTimeCode: async ({
    oneTimeCode: one_time_code,
    brand,
    email,
    trackingPayload,
  }) => {
    let status;

    try {
      const response = await api('/users/otp/verify', {
        body: {
          email: get().user.email || email,
          brand: get().brand || brand,
          one_time_code,
        },
      });
      // track the start of the one time code verification process
      get().track(trackingPayload);

      if (response?.success) {
        get().track({
          action: 'SignIn:Success',
          method: 'VerifyOTC:SubmitButtonClick',
          authState: 'authenticated',
          userId: response?.data?.user_id,
        });

        get().fetchUserProfile(response?.data?.access_token);
        get().setTinypassToken(response.data?.tinypass_token);
        status = await get().getLoginSuccessScreen(response?.data);
      } else {
        // something went wrong with the api request, set an error
        set({ error: 'There was an error verifying the code' }, false, 'verifyOneTimeCode');
      }
    } catch (err) {
      set({ error: 'There was an error verifying the code' }, false, 'verifyOneTimeCode');
      return status;
    }
    return status;
  },

  /**
   * Determines the login success screen based on user data.
   * @param {object} data - The user data.
   * @param {string} brand
   * @returns {string} The screen to display.
   */
  getLoginSuccessScreen: async (data, brand) => {
    let screen;
    if (data?.email_verified) {
      if (getFeatureConfigForBrand(MYNEWS_ENABLED, brand)?.accountCreationSuccesstype === 'news') {
        screen = SCREENS.SELECT_INTERESTS;
      } else {
        // prompt user to enter their name if they haven't already
        screen = data?.first_name ? SCREENS.LOGIN_SUCCESS : SCREENS.USER_NAME;
      }
    } else {
      // prompt user to verify email if they haven't already
      screen = SCREENS.VERIFY_OTC;
    }
    return screen;
  },


  /** *** LOG OUT USER *** */

  /**
   * Clears the user token from the store and the cookie
   * @param {object} props - The signout props.
   * @param {object} props.trackingPayload - The tracking payload object.
   * @param {string} props.trackingPayload.action - The action to track.
   * @param {string} props.trackingPayload.method - The method to track.
   * @returns {string} The status of the signout.
   */
  userSignout: async () => {
    let status;
    get().setLoading(true);
    try {
      // track the start of the signout process
      get().track({
        action: 'SignOut:Start',
        method: 'SignOutButton:Click',
      });
      const response = await api(`/users/${get().user.user_id}/signout`, {
        body: {
          bedrock_session: getCookie('bedrock_session'),
          brand: get().brand,
        },
      });
      if (response?.success) {
        get().setAuthenticationState(UNAUTHENTICATED);
        // track successful signout
        const successPayload = {
          action: 'SignOut:Success',
          method: 'SignOutButton:Click',
          signOutSuccess: true,
        };
        get().track(successPayload);
        status = SCREENS.START;
        get().setLoading(false);
      } else {
        // something went wrong with the api request, set an error
        set({ error: response?.error?.user_message }, false, 'userSignout');
        get().setLoading(false);
      }
      return status;
    } catch (err) {
      set({ error: err.message }, false, 'userSignout');
    } finally {
      get().setLoading(false);
    }
    return status;
  },


  /** *** USER PROFILE *** */

  /**
   * Fetches the user profile based on the user id
   * @param {string} access_token - The access token to restore the session
   * @returns {void}
   */
  fetchUserProfile: async (access_token) => {
    get().setLoading(true);

    // Early return if no token is provided
    if (!access_token) {
      get().setAuthenticationState(UNAUTHENTICATED);
      get().setLoading(false);
      return;
    }

    // Get the user id and brand from the token and fetch profile
    const { sub: user_id, brand } = decodeJWT(access_token);

    // Early return if no user id is found in token
    if (!user_id) {
      get().setAuthenticationState(UNAUTHENTICATED);
      get().setLoading(false);
      return;
    }

    // Fetch the user profile
    try {
      const response = await api(`/users/${user_id}/profile`, {
        body: {
          ...get().getAuthTokens(),
          brand,
        },
      });

      if (response.success) {
        const userData = response.data;
        set({ user: userData, brand }, false, 'fetchUserProfile');
        get().setAuthenticationState(AUTHENTICATED);
        // set the user_name cookie
        if (userData.first_name) {
          setCookie('user_name', userData.first_name);
        } else {
          // clear if no first name is available
          deleteCookie('user_name');
        }
      } else {
        get().setAuthenticationState(UNAUTHENTICATED);
      }
    } catch (err) {
      get().setAuthenticationState(UNAUTHENTICATED);
    }
    get().setLoading(false);
  },

  /**
   * Updates the user profile
   * @param {object} profileUpdateObj - The profile update object containing user details.
   * @param {string} [profileUpdateObj.first_name] - The user first name.
   * @param {string} [profileUpdateObj.last_name] - The user last name.
   * @param {string} [profileUpdateObj.gender] - The user gender.
   * @param {string} [profileUpdateObj.birth_year] - The user birth year.
   * @param {string} [profileUpdateObj.phone_number] - The user phone number.
   * @param {string} [profileUpdateObj.address_line1] - The user address.
   * @param {string} [profileUpdateObj.address_line2] - The user address.
   * @param {string} [profileUpdateObj.zipcode] - The user zipcode.
   * @param {string} [profileUpdateObj.state] - The user state.
   */
  updateUserProfile: async (profileUpdateObj = {}) => {
    const status = {};
    get().setLoading(true);
    try {
      await get().refreshUserToken();
    } catch (err) {
      set({ error: err.message }, false, 'getUserTokenError');
      get().setLoading(false);
      return err.message;
    }

    const body = Object.fromEntries(
      Object.entries(profileUpdateObj).filter(
        ([key, value]) => ALLOWED_PROFILE_FIELDS.includes(key) && value !== undefined,
      ),
    );

    try {
      const response = await api(`/users/${get().user.user_id}/profile`, {
        method: 'PATCH',
        headers: {
          'x-auth-token': get().access_token,
        },
        body: {
          ...body,
          ...get().getAuthTokens(),
          brand: get().brand,
        },
      });

      if (response?.success) {
        get().fetchUserProfile(get().getAuthTokens().access_token);
        // set status of screen, for login flow
        status.screen = get().signInType === 'registration'
          ? SCREENS.REGISTRATION_SUCCESS
          : SCREENS.LOGIN_SUCCESS;
        status.success = true;
      } else {
        // Set error message from API response if update fails.
        set({ error: response?.error?.user_message }, false, 'updateUserProfileError');
      }
    } catch (err) {
      // Handle network or unexpected errors.
      set({ error: err.message }, false, 'updateUserProfileError');
    } finally {
      get().setLoading(false);
    }
    return status;
  },

  /**
 * Updates the user's password
 * @param {object} params
 * @param {string} params.old_password - Current password (required)
 * @param {string} params.new_password - New password (required)
 * @returns {Promise<{success:boolean, message?:string, error?:string}>}
 */
  updateUserPassword: async ({ old_password, new_password } = {}) => {
    set({ loading: true, error: '' }, false, 'updateUserPassword:start');

    try {
      if (!old_password || !new_password) {
        const msg = 'old_password and new_password are required';
        set({ error: msg }, false, 'updateUserPassword:validation');
        return { success: false, error: msg };
      }

      try {
        await get().refreshUserToken();
      } catch (err) {
        set({ error: err.message }, false, 'updateUserPassword:refreshError');
        return { success: false, error: err.message };
      }

      const { user_id } = get().user || {};

      if (!user_id) {
        const message = 'No user in session';
        set({ error: message }, false, 'updateUserPassword:noUser');
        return { success: false, error: message };
      }

      const resp = await api(`/users/${user_id}/password`, {
        method: 'PATCH',
        headers: {
          'x-auth-token': get().access_token,
        },
        body: {
          old_password,
          new_password,
          ...get().getAuthTokens(),
          brand: get().brand,
        },
      });

      if (resp && resp.success) {
        return { success: true, message: resp?.data?.message || 'Password changed successfully.' };
      }

      const errorMessage = resp?.error?.user_message || resp?.error?.message || 'Unable to change password.';
      set({ error: errorMessage }, false, 'updateUserPassword:error');
      return { success: false, error: errorMessage };
    } catch (err) {
      const message = String(err);
      set({ error: message }, false, 'updateUserPassword:catch');
      return { success: false, error: message };
    } finally {
      set({ loading: false }, false, 'updateUserPassword:finally');
    }
  },

  /** *** USER NEWSLETTER SUBSCRIPTIONS *** */

  /**
   * Fetches the user's newsletter subscriptions
   * @returns {Promise<object>} The response from the API including subscriptions array.
   */
  fetchNewsletterSubscriptions: async () => {
    set({ loading: true }, false, 'fetchNewsletterSubscriptions:start');
    try {
      const userId = get().user.user_id;
      get().track({ action: 'Fetch:Newsletters', method: 'fetchNewsletterSubscriptions', userId });

      const response = await api(`/users/${userId}/subscriptions/newsletters`, {
        method: 'POST',
        body: {
          ...get().getAuthTokens(),
          brand: get().brand,
        },
      });

      if (response?.success) {
        set({ newsletterSubscriptions: response.data.subscriptions }, false, 'fetchNewsletterSubscriptions:success');
      } else {
        set({ error: response.error?.user_message || 'Failed to fetch subscriptions' }, false, 'fetchNewsletterSubscriptions:error');
      }

      return response;
    } catch (err) {
      set({ error: err.message }, false, 'fetchNewsletterSubscriptions:error');
      return { success: false, error: { user_message: err.message } };
    } finally {
      set({ loading: false }, false, 'fetchNewsletterSubscriptions:finally');
    }
  },

  /**
   * Helper to handle newsletter subscription mutations
   * @param {obj} props
   * @param {Array} props.subscriptions - { [id]: boolean } mapping of newsletter IDs to subscription state
   * @param {obj} props.optimisticState - object for optimistic set
   * @param {obj} props.revertState - object to revert to on failure
   * @param {string} props.action - label for logging, e.g. 'toggleNewsletterSubscription'
   */
  updateNewsletterSubscriptions: async ({
    subscriptions,
    optimisticState,
    revertState,
    action,
  }) => {
    const {
      user: { user_id },
      refreshUserToken,
      getAuthTokens,
      brand,
    } = get();

    set(optimisticState, false, `${action}:optimistic`);

    try {
      await refreshUserToken();

      const body = {
        ...getAuthTokens(),
        brand,
        source: 'newsletters',
        location_source: 'account_page',
        subscriptions,
      };

      const { success, error } = await api(
        `/users/${user_id}/subscriptions/newsletters`,
        { method: 'PATCH', body },
      );

      if (!success) {
        throw new Error(error?.user_message || 'Failed to update subscriptions');
      }
    } catch (err) {
      set(revertState, false, `${action}:revert`);
      set({ error: err.message }, false, `${action}:error`);
      // eslint-disable-next-line no-console
      console.error(`Newsletter ${action} failed:`, err);
    }
  },

  /**
   * @param {number} newsletterId - The ID of the newsletter to toggle subscription for.
   */
  toggleNewsletterSubscription: async (newsletterId) => {
    const previousSubs = get().newsletterSubscriptions;
    const wasSubscribed = previousSubs.includes(newsletterId);
    const updatedSubsArray = wasSubscribed
      ? previousSubs.filter((id) => id !== newsletterId) // remove id from subs array
      : [...previousSubs, newsletterId]; // add id to subs array

    const updatedSubsObj = { [newsletterId]: !wasSubscribed };
    const updateObj = {
      subscriptions: updatedSubsObj,
      optimisticState: { newsletterSubscriptions: updatedSubsArray },
      revertState: {
        newsletterSubscriptions: previousSubs,
        hasSubscribedToAll: get().hasSubscribedToAll,
      },
      action: 'toggleNewsletterSubscription',
    };
    if (wasSubscribed) {
      // if they are unsubbing from this newsletter we can set hasSubscribedToAll to false
      updateObj.optimisticState.hasSubscribedToAll = false;
    }
    await get().updateNewsletterSubscriptions(updateObj);
  },

  /**
   * Subscribes the user to all newsletters in the newsletterData array.
   * @param {import('lib/graphqlTypeDefs.mjs').OrganizationType} brand - The brand name associated with the user.
   */
  subscribeToAllNewsletters: async (brand) => {
    const previousSubs = get().newsletterSubscriptions;
    const allNewsletterIDs = newsletterData[brand].map(({ newsletterId }) => newsletterId);
    const allSubsTrueObj = Object.fromEntries(allNewsletterIDs.map((id) => [id, true]));
    if (previousSubs.length === allNewsletterIDs.length) {
      // because the initial state of the button is 'on' if they are already subscribed to all NLs, fire nothing and set hasSubscribedToAll to true
      set({ hasSubscribedToAll: true }, false, 'subscribeToAllNewsletters');
      return;
    }
    await get().updateNewsletterSubscriptions({
      subscriptions: allSubsTrueObj,
      optimisticState: {
        newsletterSubscriptions: allNewsletterIDs,
        hasSubscribedToAll: true,
      },
      revertState: {
        newsletterSubscriptions: previousSubs,
        hasSubscribedToAll: get().hasSubscribedToAll,
      },
      action: 'subscribeToAllNewsletters',
    });
  },

  /**
   * Unsubscribes the user from all newsletters in the newsletterData array.
   * @param {import('lib/graphqlTypeDefs.mjs').OrganizationType} brand - The brand name associated with the user.
   */
  unsubscribeFromAllNewsletters: async (brand) => {
    const previousSubs = get().newsletterSubscriptions;
    const allNewsletterIDs = newsletterData[brand].map(({ newsletterId }) => newsletterId);
    const allSubsFalseObj = Object.fromEntries(allNewsletterIDs.map((id) => [id, false]));

    if (previousSubs.length === 0) {
      // if they have nothing to unsub from, do nothing
      return;
    }
    await get().updateNewsletterSubscriptions({
      subscriptions: allSubsFalseObj,
      optimisticState: {
        newsletterSubscriptions: [],
        hasSubscribedToAll: false,
      },
      revertState: { newsletterSubscriptions: previousSubs },
      action: 'unsubscribeFromAllNewsletters',
    });
  },

  /** *** USER PREFERENCES *** */

  /**
   * Saves the user's interests in various types of content
   *
   * @param {string[]} interests
   */
  updateContentInterests: async (interests) => {
    const {
      brand,
      getAuthTokens,
      user: { user_id },
    } = get();
    const { bedrock_session, access_token } = getAuthTokens();

    get().setLoading(true);

    try {
      if (!bedrock_session || !access_token) {
        throw new Error('Cannot update interests without a valid session and access token');
      }

      if (!user_id) {
        throw new Error('Cannot update interests without a valid user id');
      }

      await updateUserPreferences({
        user_id,
        bedrock_session,
        access_token,
        brand,
        category: 'preferences',
        values: { contentInterests: interests },
      });
    } catch (err) {
      set({ error: String(err) }, false, 'updatePreferencesError');
    } finally {
      get().setLoading(false);
    }
  },

  /**
   * Updates the user's email address
   * @param {string} newEmail
   * @param {string} password
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  updateUserEmail: async (newEmail, password) => {
    set({ loading: true }, false, 'updateUserEmail:start');

    try {
      await get().refreshUserToken();
    } catch (err) {
      set({ error: err.message }, false, 'updateUserEmail:refreshError');
      return { success: false, error: err.message };
    }

    try {
      const response = await api(`/users/${get().user.user_id}/email`, {
        method: 'PATCH',
        body: {
          ...get().getAuthTokens(),
          old_email: get().user.email,
          new_email: newEmail,
          brand: get().brand,
          password,
        },
      });

      if (response?.success) {
        set({ user: { ...get().user, email: newEmail, email_verified: false }, error: '' }, false, 'updateUserEmail:success');
        return { success: true };
      }

      const errorMessage = response?.error?.user_message || response?.error?.message || 'Unable to update email.';
      set({ error: errorMessage }, false, 'updateUserEmail:error');
      return { success: false, error: errorMessage };
    } catch (err) {
      set({ error: err.message }, false, 'updateUserEmail:error');
      return { success: false, error: err.message };
    } finally {
      set({ loading: false }, false, 'updateUserEmail:finally');
    }
  },

  /** *** ANALYTICS **** */

  /**
   * Tracks user events and sends them to the analytics framework.
   * @param {object} payload - The payload to track.
   * @param {string} payload.action - The action to track.
   * @param {string} payload.method - The method to track.
   * @param {string} payload.userId - The user ID to track, named userId instead of user_id so as to be overwriteable in obj destructuring.
   * @returns {void}
   */
  track: (payload) => {
    // Extract user information
    const userId = payload.userId || get().user.user_id || null;
    const authState = userId ? AUTHENTICATED : UNAUTHENTICATED;

    // Format screen name with first letter capitalized
    const capitalizedScreen = get().screen.charAt(0).toUpperCase() + get().screen.slice(1);

    // Determine if this is a sign-out action
    const isSignOutSuccess = payload.signOutSuccess;

    // Determine the correct page view for the event, signout only comes from AccountPage
    const pageView = isSignOutSuccess ? 'AccountPage' : get().pageView;

    // Build the method string only if method exists in payload
    let method = null;
    if (payload.method) {
      const overlayPrefix = get().showAuthenticationModal ? 'ModalOverlay:' : '';
      const baseMethod = `${overlayPrefix}${capitalizedScreen}:${payload.method}`;
      method = pageView ? `${pageView}:${baseMethod}` : baseMethod;
    }

    // Prepare the final tracking payload
    const finalPayload = {
      ...payload,
      userId,
      authState,
      method,
    };

    // Send the event to the analytics framework
    $t('track', 'ramen_authflow', finalPayload);
  },
  /**
   * Sets the pageView, used in analytics tracking
   * @param {string} pageView - The page view.
   * @returns {void}
   */
  setPageView: (pageView) => {
    set({ pageView }, false, 'setPageView');
  },

});

/**
 * Enable the devtools in development mode only
 */
const isDevelop = process.env.NODE_ENV === 'development';

const devToolsProps = {
  name: 'useBedrockRegistration',
  anonymousActionType: 'action',
  serialize: true,
  // eslint-disable-next-line jsdoc/require-jsdoc
  actionSanitizer: (action) => ({
    ...action,
    type: `BedrockRegistration/${action.type}`,
  }),
};

// eslint-disable-next-line jsdoc/require-jsdoc
const middlewares = (f) => (isDevelop ? devtools(f, devToolsProps) : f);

/* Create Store */
export const useBedrockRegistration = create(middlewares(bedrockRegistrationStore));
